import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import HandshakeIcon from '@mui/icons-material/Handshake';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HistoryIcon from '@mui/icons-material/History';
import TableChartIcon from '@mui/icons-material/TableChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ChatIcon from '@mui/icons-material/Chat';
import InsightsIcon from '@mui/icons-material/Insights';
import { useAuth } from '../../hooks/useAuth';
import { useRole, hasMinimumRole, type Role } from '../../hooks/useRole';

const DRAWER_WIDTH = 240;

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  minRole: Role;
}

const navItems: NavItem[] = [
  { to: '/innsikt', label: 'Innsikt', icon: <InsightsIcon />, minRole: 'Reader' },
  { to: '/agreements', label: 'Avtaler', icon: <HandshakeIcon />, minRole: 'Reader' },
  { to: '/income-lines', label: 'Inntektslinjer', icon: <TableChartIcon />, minRole: 'Reader' },
  { to: '/dimensions', label: 'Dimensjoner', icon: <AccountTreeIcon />, minRole: 'Reader' },
  { to: '/quarantine', label: 'Karantene', icon: <WarningAmberIcon />, minRole: 'Reader' },
  { to: '/upload', label: 'Last opp inntektsgrunnlag', icon: <UploadFileIcon />, minRole: 'Editor' },
  { to: '/budget-import', label: 'Budsjettimport', icon: <CloudUploadIcon />, minRole: 'Admin' },
  { to: '/avsjekk', label: 'Avsjekk', icon: <FactCheckIcon />, minRole: 'Reader' },
  { to: '/chat', label: 'Assistent', icon: <ChatIcon />, minRole: 'Reader' },
  { to: '/audit', label: 'Logg', icon: <HistoryIcon />, minRole: 'Reader' },
];

const roleColor: Record<Role, 'error' | 'primary' | 'default'> = {
  Admin: 'error',
  Editor: 'primary',
  Reader: 'default',
  None: 'default',
};

export function AppDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { account, logout } = useAuth();
  const role = useRole();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Typography variant="h6" noWrap sx={{ fontWeight: 700, color: 'primary.main' }}>
          InntektsAnalyse
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1 }}>
        {navItems
          .filter((item) => hasMinimumRole(role, item.minRole))
          .map((item) => (
            <ListItem key={item.to} disablePadding>
              <ListItemButton
                component={NavLink}
                to={item.to}
                onClick={isMobile ? handleDrawerToggle : undefined}
                sx={{
                  '&.active': {
                    bgcolor: 'primary.dark',
                    color: 'primary.contrastText',
                    '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                  },
                  '&.active:hover': {
                    bgcolor: 'primary.main',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          {account?.name || account?.username || '...'}
        </Typography>
        {role && <Chip label={role} color={roleColor[role]} size="small" sx={{ mt: 0.5 }} />}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar — visible on mobile */}
      <AppBar
        position="fixed"
        sx={{
          display: { md: 'none' },
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} aria-label="Meny" sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            InntektsAnalyse
          </Typography>
          <Button color="inherit" onClick={logout} size="small">
            Logg ut
          </Button>
        </Toolbar>
      </AppBar>

      {/* Sidebar drawer */}
      <Box component="nav" aria-label="Hovedmeny" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {/* Mobile: temporary drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop: permanent drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
          }}
          open
        >
          {drawerContent}
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
            <Button fullWidth size="small" onClick={logout} color="inherit">
              Logg ut
            </Button>
          </Box>
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          bgcolor: 'background.default',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Spacer for mobile AppBar */}
        <Toolbar sx={{ display: { md: 'none' } }} />

        <Box sx={{ flex: 1, p: 3, maxWidth: 1400, width: '100%', mx: 'auto' }}>
          <Outlet />
        </Box>

        <Box
          component="footer"
          sx={{
            py: 2,
            px: 3,
            textAlign: 'center',
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Kristiansund kommune — InntektsAnalyse Admin
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
